import { useState } from 'react'
import { CHART_COLORS } from '../utils/chartColors'
import { Z_PANEL } from './zLayers'

const C = {
  text: CHART_COLORS.text,
  textSecond: CHART_COLORS.textMuted,
  border: CHART_COLORS.border,
  primary: CHART_COLORS.primary,
  bg: CHART_COLORS.surface,
  thumbOff: CHART_COLORS.textMuted,
  thumbOn: CHART_COLORS.white,
  trackOff: CHART_COLORS.border,
  trackOn: CHART_COLORS.primary,
}

export type SyncTogglesKey =
  | 'symbol'
  | 'interval'
  | 'crosshair'
  | 'crosshairGlobal'
  | 'time'
  | 'window'

export interface ChartSyncTogglesValue {
  symbol?: boolean
  interval?: boolean
  /** Same-ticker crosshair channel: mirror the crosshair between charts of the
   *  same symbol (workspace-wide). Default on. */
  crosshair?: boolean
  /** Global crosshair channel: also mirror by time across every chart with this
   *  on, regardless of ticker — the cross-ticker "sympathy" club. Default off. */
  crosshairGlobal?: boolean
  time?: boolean
  /** Anchored grids only: scroll and zoom together by time of day, each chart
   *  on its own event day. Replaces `time`, which cannot line up charts that
   *  sit on different days. */
  window?: boolean
}

export interface SyncToggleLock {
  /** Forces the toggle to a fixed value and disables interaction. */
  value: boolean
  /** Shown in a tooltip when the user hovers the lock icon. */
  reason: string
}

export type SyncToggleLocks = Partial<Record<SyncTogglesKey, SyncToggleLock>>

interface ChartSyncTogglesProps {
  value: ChartSyncTogglesValue
  onChange: (next: ChartSyncTogglesValue) => void
  /** Per-toggle locks. Locked toggles render disabled with an Info tooltip. */
  locks?: SyncToggleLocks
  /** Rows not to render. Defaults to `['window']`: only anchored grids offer it. */
  hiddenToggles?: readonly SyncTogglesKey[]
}

const DEFAULT_HIDDEN: readonly SyncTogglesKey[] = ['window']

const TOGGLES: ReadonlyArray<{ key: SyncTogglesKey; label: string; description: string }> = [
  {
    key: 'symbol',
    label: 'Symbol',
    description: 'Share the same ticker across every chart in this layout.',
  },
  {
    key: 'interval',
    label: 'Interval',
    description: 'Share the same timeframe across every chart in this layout.',
  },
  {
    key: 'crosshair',
    label: 'Crosshair (same ticker)',
    description: 'Mirror the crosshair between charts of the same ticker, anywhere.',
  },
  {
    key: 'crosshairGlobal',
    label: 'Crosshair (global)',
    description:
      'Also mirror the crosshair by time across every chart with this on — across different tickers, for sympathy plays.',
  },
  {
    key: 'time',
    label: 'Date range',
    description: 'Share the visible date range across every chart in this layout.',
  },
  {
    key: 'window',
    label: 'Visible window',
    description: 'Scroll and zoom every chart together by time of day, each on its own event day.',
  },
]

/**
 * "Sync in layout" toggles. Every row gets an info
 * icon: unlocked rows describe what the sync option does, locked rows
 * explain why they can't be changed (e.g. Chart Scanner forces synced
 * intervals across every cell).
 */
export function ChartSyncToggles({
  value,
  onChange,
  locks,
  hiddenToggles = DEFAULT_HIDDEN,
}: ChartSyncTogglesProps) {
  return (
    <div
      data-testid="chart-sync-toggles"
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      {TOGGLES.filter(({ key }) => !hiddenToggles.includes(key)).map(
        ({ key, label, description }) => {
          const lock = locks?.[key]
          const checked = lock ? lock.value : (value[key] ?? false)
          // Locked rows show the lock reason; everything else shows the
          // generic description of what this sync option does.
          const tooltipText = lock?.reason ?? description
          return (
            <div
              key={key}
              data-testid={`chart-sync-toggle-row-${key}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '2px 4px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: C.text }}>{label}</span>
                <InfoTooltip reason={tooltipText} testKey={key} label={label} />
              </div>
              <Switch
                checked={checked}
                disabled={Boolean(lock)}
                onChange={(next) => {
                  if (lock) return
                  onChange({ ...value, [key]: next })
                }}
                testId={`chart-sync-toggle-${key}`}
                ariaLabel={`Sync ${label}`}
              />
            </div>
          )
        },
      )}
    </div>
  )
}

function Switch({
  checked,
  disabled,
  onChange,
  testId,
  ariaLabel,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
  testId: string
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: 28,
        height: 16,
        borderRadius: 999,
        background: checked ? C.trackOn : C.trackOff,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        padding: 0,
        transition: 'background 0.15s',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 14 : 2,
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: checked ? C.thumbOn : C.thumbOff,
          transition: 'left 0.15s, background 0.15s',
        }}
      />
    </button>
  )
}

function InfoTooltip({
  reason,
  testKey,
  label,
}: {
  reason: string
  testKey: string
  label: string
}) {
  const [hover, setHover] = useState(false)
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label={`${label} sync info`}
        data-testid={`chart-sync-lock-info-${testKey}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: C.textSecond,
          cursor: 'help',
        }}
      >
        <InfoIcon size={11} />
      </button>
      {hover && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: 6,
            width: 220,
            padding: '4px 8px',
            fontSize: 11,
            color: C.text,
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            boxShadow: '0 6px 12px rgba(0,0,0,.3)',
            zIndex: Z_PANEL,
            whiteSpace: 'normal',
            pointerEvents: 'none',
          }}
        >
          {reason}
        </span>
      )}
    </span>
  )
}

function InfoIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  )
}
